import React, { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Medical supplies handed to a patient that were never written on their prescription: a syringe for
// an injection, the lancet and strip of a bedside sugar check, a dressing, gloves. The counter puts
// them on the bill, and this is the only place that reads them back together.
//
// Every row here is by construction "not prescribed": a line carries a supply only when it has no
// prescribed medicine behind it, so there is nothing to compare against and no filter to get wrong.
//
// Who a supply went to comes only with the OPD, Clinical or Finance reports permission, and what it
// cost only with Finance: an Inventory-only reader sees what was given and how many. The server
// leaves the rest out, and this screen drops the matching columns.

const IDENTITY_KEYS = new Set(['mrn', 'patient', 'prescriptionNumber']);

const TYPE_LABEL = (t) => (t ? t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '');

const DETAIL_COLUMNS = [
  { key: 'invoiceDate', label: 'Date', type: 'date' },
  { key: 'invoiceNumber', label: 'Invoice #', type: 'mono' },
  { key: 'mrn', label: 'MR #', type: 'mono' },
  { key: 'patient', label: 'Patient' },
  { key: 'supply', label: 'Supply' },
  { key: 'supplyType', label: 'Kind', render: (r) => TYPE_LABEL(r.supplyType), csv: (r) => TYPE_LABEL(r.supplyType) },
  { key: 'quantity', label: 'Qty', align: 'right' },
  {
    key: 'amount', label: 'Amount', align: 'right',
    render: (r) => <span className="font-semibold">{fmtMoney(r.amount)}</span>,
    csv: (r) => r.amount,
  },
  {
    key: 'prescriptionNumber', label: 'Rx #', type: 'mono',
    render: (r) => r.prescriptionNumber || <span className="text-gray-300">—</span>,
    csv: (r) => r.prescriptionNumber || '',
  },
  {
    // Stock only moves when the bill is paid, so an unpaid line has not left the shelf yet.
    key: 'paidAt', label: 'Handed over',
    render: (r) => (r.paidAt
      ? <span className="text-emerald-700 font-medium">Yes</span>
      : <span className="text-amber-700 font-medium">Not yet</span>),
    csv: (r) => (r.paidAt ? 'Yes' : 'Not yet'),
  },
];

const SUMMARY_COLUMNS = [
  { key: 'supply', label: 'Supply' },
  { key: 'supplyType', label: 'Kind', render: (r) => TYPE_LABEL(r.supplyType), csv: (r) => TYPE_LABEL(r.supplyType) },
  { key: 'units', label: 'Units', align: 'right' },
  { key: 'bills', label: 'Bills', align: 'right' },
  {
    key: 'amount', label: 'Amount', align: 'right',
    render: (r) => <span className="font-semibold">{fmtMoney(r.amount)}</span>,
    csv: (r) => r.amount,
  },
];

const SuppliesGivenReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getSuppliesGiven({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const bySupply = (data?.bySupply || []).map((r) => ({ ...r, units: Number(r.units) }));
  const k = data?.kpis || {};
  const identity = data?.access?.identity !== false;
  const money = data?.access?.money !== false;
  const keep = (c) => (identity || !IDENTITY_KEYS.has(c.key)) && (money || c.key !== 'amount');
  const detailColumns = DETAIL_COLUMNS.filter(keep);
  const summaryColumns = SUMMARY_COLUMNS.filter(keep);
  const hidden = [
    !identity && 'Who each supply went to is shown to users with the OPD & Patients, Clinical or Finance reports permission.',
    !money && 'Amounts are shown to users with the Finance reports permission.',
  ].filter(Boolean).join(' ');

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip
        items={[
          { label: 'Supply lines', value: k.lines ?? 0 },
          { label: 'Units given', value: k.units ?? 0 },
          { label: 'Patients', value: k.patients ?? 0 },
          ...(money ? [{ label: 'Value', value: fmtMoney(k.amount) }] : []),
          { label: 'Handed over', value: k.handedOver ?? 0, hint: 'On a bill that has been paid' },
        ]}
      />

      <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Supplies put on a bill at the counter, none of which were written on the prescription. Stock
        comes off the shelf when the bill is paid, so a line that has not been paid has not been
        handed over yet.
      </p>

      <ChartPanel title="Units by supply">
        {loading || bySupply.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No supplies were added to a bill in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, bySupply.length * 40)}>
            <BarChart data={bySupply} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} />
              <YAxis type="category" dataKey="supply" {...axisProps} width={170} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => (n === 'Amount' ? fmtMoney(v) : v)} />
              <Bar dataKey="units" name="Units" radius={[0, 4, 4, 0]} barSize={18}>
              {bySupply.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        title="By supply"
        columns={summaryColumns}
        rows={data?.bySupply || []}
        csvName={`supplies-given-summary-${range.from}-to-${range.to}`}
        emptyText="No supplies were added to a bill in the selected range"
      />

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'invoice', value: r.invoiceNumber })}
        title="Every supply given"
        columns={detailColumns}
        rows={data?.rows || []}
        searchable
        csvName={`supplies-given-${range.from}-to-${range.to}`}
        emptyText="No supplies were added to a bill in the selected range"
        footnote={hidden || undefined}
      />
      {drill.node}
    </div>
  );
};

export default SuppliesGivenReport;
